import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  RUNNING_BUILD_ID,
  fetchDeployedBuildId,
  isNewerBuild,
  mayAutoReload,
  reloadForBuild,
  reloadedForBuild,
} from "../../lib/buildVersion";

const CHECK_EVERY_MS = 5 * 60_000;

/**
 * Picks up a deploy without a hard refresh. Checks when the tab comes back into view and every few minutes;
 * once a newer build is out it reloads at the next page change — the page is being left anyway, so nothing
 * typed is lost — and meanwhile offers a Reload button for someone who stays put on one screen.
 */
export function NewVersionPrompt() {
  const location = useLocation();
  const [deployed, setDeployed] = useState<string | null>(null);
  const firstLocation = useRef(true);

  useEffect(() => {
    if (import.meta.env.DEV) return; // the dev server has no version.json, and HMR already keeps it current

    let cancelled = false;
    const check = async () => {
      const id = await fetchDeployedBuildId();
      if (!cancelled && isNewerBuild(RUNNING_BUILD_ID, id)) setDeployed(id);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };

    void check();
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (firstLocation.current) {
      firstLocation.current = false;
      return;
    }
    if (deployed && mayAutoReload(deployed, reloadedForBuild())) reloadForBuild(deployed);
    // Only a page change should trigger this; `deployed` arriving must not reload out from under someone.
  }, [location.pathname]);

  if (!deployed) return null;

  return (
    <div
      role="status"
      style={{
        position: "fixed",
        left: "50%",
        bottom: 16,
        transform: "translateX(-50%)",
        zIndex: 2100,
        display: "flex",
        alignItems: "center",
        gap: 12,
        maxWidth: "calc(100vw - 32px)",
        padding: "8px 8px 8px 14px",
        fontSize: 13,
        background: "var(--surface)",
        color: "var(--text)",
        border: "var(--border-w) solid var(--border)",
        borderRadius: 10,
        boxShadow: "var(--shadow-lg)",
      }}
    >
      <span>A new version of KeyDials is available.</span>
      <button
        type="button"
        onClick={() => reloadForBuild(deployed)}
        style={{
          padding: "5px 12px",
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 8,
          border: "none",
          background: "var(--accent)",
          color: "var(--on-accent)",
          cursor: "pointer",
        }}
      >
        Reload
      </button>
    </div>
  );
}
